// AppLock context provider — deferred-encryption pattern (visual mode).
//
// Data sensitivity assessment:
//   Backsolve stores sensitive financial data (cap tables, valuation
//   inputs/results) in IndexedDB, plus UI preferences in localStorage.
//   Per the decision matrix this would normally select `encrypted` mode.
//   However, the persistence layer relies on IDB indexes (byEntity, byStatus,
//   byDate, byValuation) and cursor iteration, which the `lock.idb` record API
//   does not support. The Zustand store is also created eagerly at module
//   import — before any lock can be initialised — so encrypted IDB rehydration
//   is architecturally incompatible without a large refactor.
//   `visual` mode is therefore the appropriate tier: it adds the lock-screen
//   UX (AppLock's primary feature) with zero risk to the existing index-based
//   persistence layer. Storage migration is NOT required for visual mode
//   (INTEGRATION.md §22).
//
// Follows INTEGRATION.md Step 2b (React integration pattern):
//   - ui: false (we render our own screens)
//   - isConfigured initialises as false (real value comes from the lock instance)
//   - `uninitialized` lock state is treated as unlocked
//   - clearAppLockMetaKeys() runs on every skip/enable transition

import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';

const DEFERRED_KEY = 'backsolve:app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

// AppLock meta-keys persisted in localStorage. These must be cleared on state
// transitions or stale `isConfigured = true` values cause the LockScreen to
// render instead of the SetupScreen on first load.
const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys(): void {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  try {
    const stored = localStorage.getItem(DEFERRED_KEY);
    if (stored === 'skipped') return 'skipped';
    if (stored === 'enabled') return 'enabled';
  } catch {
    // localStorage may be unavailable (private mode) — treat as pending.
  }
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'visual',
    appRootSelector: '#root',
    lockOnHide: false,
    idleTimeout: 900,
    ui: false,
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

export interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

// CRITICAL: isConfigured must ALWAYS initialise as false. The real value is
// reported by the lock instance after it is created. Seeding it from `decision`
// causes the LockScreen to render instead of SetupScreen on first load.
function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false,
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  const skipEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'skipped');
    } catch {
      // ignore quota / private mode
    }
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  const enableEncryption = useCallback(() => {
    try {
      localStorage.setItem(DEFERRED_KEY, 'enabled');
    } catch {
      // ignore quota / private mode
    }
    clearAppLockMetaKeys();
    setState((prev) => ({
      ...prev,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    const instance = createLock();
    instanceRef.current = instance;

    const onLock = () => {
      setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState((prev) => ({
        ...prev,
        isLocked: false,
        isUnlocked: true,
        isConfigured: true,
      }));
    };
    const onPasswordClear = () => {
      setState((prev) => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState((prev) => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    // `uninitialized` means no password has been set yet — treat it as
    // unlocked so the SetupScreen renders (not the LockScreen).
    setState((prev) => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked:
        instance.state === 'unlocked' || instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    return () => {
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  // Loading state while the lock instance is being created.
  if (state.decision === 'enabled' && !state.lock) return null;

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
